import {t} from 'i18next';
import React, {useEffect, useState} from 'react';
import {toast} from 'react-toastify';
import {useDebouncedCallback} from 'use-debounce';
import {z} from 'zod';
import {type TypedTranslation} from '../../../../../i18n';
import {MAX_VIDEO_SIZE, NCC_TUTORIAL_VIDEO_URL} from '../../../../../constants';
import Modal from '../../../../../incase-domain/components/modals/directory/Modal';
import {IElementVideo, VideoSourceType} from '../../../../../incase-domain/elements/video';
import {validateEmbeddedVideo} from '../../../../../incase-domain/validators/elements/video';
import {uploadVideoFile} from '../../../../../service/files.service';
import {getVideoWidthHeightRatio} from '../../../../../utils/getVideoWidthHeightRatio';
import {formatBytes} from '../../../../../utils/utils';
import FileUpload from '../../../FileUpload';
import UnderlinedText from '../../../UnderlinedText';
import Label from '../../../../../incase-domain/components/label/Label';

interface VideoElementModalProps {
    isOpen?: boolean;
    onCancel: () => void;
    onConfirm: (src: IElementVideo['videoSource'], widthHeightRatio?: number) => void;
    defaultSrc?: IElementVideo['videoSource'];
}

function VideoElementModal(props: VideoElementModalProps): JSX.Element {
    const {isOpen, onCancel, onConfirm, defaultSrc} = props;

    const [validationErrors, setValidationErrors] = useState<{valid: boolean; errors?: z.ZodError}>({valid: false});
    const [sizeError, setSizeError] = useState<boolean>(false);
    const [videoSrc, setVideoSrc] = useState<{src: string; type: VideoSourceType}>(
        defaultSrc || {
            src: '',
            type: VideoSourceType.url,
        }
    );

    useEffect(() => {
        setVideoSrc(
            defaultSrc || {
                src: '',
                type: VideoSourceType.url,
            }
        );
    }, [defaultSrc]);

    const debouncedEmbeddedUrlValidation = useDebouncedCallback((url?: string) => {
        if (url) {
            try {
                validateEmbeddedVideo(url);
                setValidationErrors({valid: true});
            } catch (errors) {
                if (errors instanceof z.ZodError) {
                    setValidationErrors({valid: false, errors});
                }
            }
        } else {
            setValidationErrors({valid: false});
        }
    }, 250);

    useEffect(() => {
        if (isOpen) return;

        setSizeError(false);
        setValidationErrors({valid: false});
        setVideoSrc({src: '', type: VideoSourceType.url});
    }, [isOpen]);

    useEffect(() => {
        if (videoSrc.type === VideoSourceType.embedded) {
            debouncedEmbeddedUrlValidation(videoSrc.src);
        }
    }, [videoSrc, debouncedEmbeddedUrlValidation]);

    function handleConfirm(): void {
        const hasSource = videoSrc.src;
        const hasUrlTypeError = videoSrc.type === VideoSourceType.url && sizeError;
        const hasEmbeddedTypeError = videoSrc.type === VideoSourceType.embedded && !validationErrors.valid;

        if (hasSource && !hasUrlTypeError && !hasEmbeddedTypeError) {
            onConfirm(videoSrc);
        } else {
            onCancel();
        }
    }

    return (
        <Modal
            isOpen={!!isOpen}
            icon="solar:video-frame-play-horizontal-bold"
            header={t('elementModal.video.header')}
            description={t('elementModal.video.description')}
            onCancel={onCancel}
            onConfirm={handleConfirm}
            confirmDisabled={!validationErrors.valid || sizeError || !videoSrc.src}
            showCancel
            showConfirm
            closeOnClickOutside={false}
        >
            <div>
                {sizeError && (
                    <strong style={{color: 'red'}}>
                        <p>{t('presEditPage.editSidebar.validation.videoSize')}</p>
                        <p>
                            {t('presEditPage.editSidebar.validation.maxSize')} {formatBytes(MAX_VIDEO_SIZE, 1, true)}
                        </p>
                    </strong>
                )}
                <div style={{height: '35vh'}}>
                    <FileUpload
                        onChange={(files) => {
                            setSizeError(false);
                            const videoFiles = Array.from(files).filter((file) => file.type.startsWith('video/'));
                            const hasVideoFile = videoFiles.length > 0;
                            if (hasVideoFile) {
                                const video = videoFiles[0];
                                if (video.size > MAX_VIDEO_SIZE) {
                                    setSizeError(true);
                                    return;
                                }

                                const objectUrl = URL.createObjectURL(video);
                                Promise.all([uploadVideoFile(video), getVideoWidthHeightRatio(objectUrl)])
                                    .then(([{url}, widthHeightRatio]) => {
                                        onConfirm(
                                            {
                                                src: url,
                                                type: VideoSourceType.url,
                                            },
                                            widthHeightRatio
                                        );
                                    })
                                    .catch(() => {
                                        toast.error(t('common.uploadFailed'));
                                    })
                                    .finally(() => {
                                        URL.revokeObjectURL(objectUrl);
                                    });
                            } else {
                                toast.error(t('elementModal.video.invalidFileType'));
                            }
                        }}
                    />
                </div>

                <div className="u-mt-4 u-mb-4">
                    <UnderlinedText text={t('common.or')} />
                </div>

                <div className={`u-mt-3 ${validationErrors.errors ? 'is-error' : ''}`}>
                    <>
                        <Label>{t('elementModal.video.enterUrl')}</Label>
                        <div className="c-sidepanel__input">
                            <input
                                value={videoSrc.type === VideoSourceType.embedded ? videoSrc.src ?? '' : ''}
                                onChange={(e) => {
                                    setVideoSrc({src: e.target.value, type: VideoSourceType.embedded});
                                }}
                                placeholder={NCC_TUTORIAL_VIDEO_URL}
                                id="video-src"
                                type="text"
                            />
                        </div>

                        {validationErrors &&
                            validationErrors.errors?.errors.map((err) => (
                                <div className="error-msg">{t(err.message as TypedTranslation)}</div>
                            ))}
                    </>
                </div>
            </div>
        </Modal>
    );
}

export default VideoElementModal;
